import { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, setDoc, onSnapshot, updateDoc, serverTimestamp, deleteDoc, getDoc } from 'firebase/firestore';

export interface QuizTrack {
  id: string;
  name: string;
  artist: string;
  albumImage: string;
  owner: 'mauricio' | 'novia';
  ownerName: string;
}

export interface PlayerData {
  joined: boolean;
  ready: boolean;
  userId: string;
}

export interface CoopQuizSession {
  sessionId: string;
  status: 'waiting' | 'ready' | 'playing' | 'finished';
  createdAt: any;
  questions: QuizTrack[];
  currentQuestion: number;
  players: {
    player1: PlayerData;
    player2: PlayerData;
  };
  answers: {
    [key: string]: {
      player1?: 'mauricio' | 'novia';
      player2?: 'mauricio' | 'novia';
    };
  };
  scores: {
    player1: number;
    player2: number;
  };
}

interface UseCoopQuizProps {
  myTracks: any[];
  herTracks: any[];
  myProfile: any;
  herProfile: any;
}

export const useCoopQuiz = ({ myTracks, herTracks, myProfile, herProfile }: UseCoopQuizProps) => {
  const [session, setSession] = useState<CoopQuizSession | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [myPlayerNumber, setMyPlayerNumber] = useState<1 | 2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper functions to get display data (always returns Spotify data, never changes)
  const getPlayer1DisplayData = () => ({
    displayName: myProfile?.display_name || 'Usuario 1',
    profileImage: myProfile?.images?.[0]?.url || 'https://via.placeholder.com/100x100/1DB954/white?text=U1'
  });

  const getPlayer2DisplayData = () => ({
    displayName: herProfile?.display_name || 'Usuario 2',
    profileImage: herProfile?.images?.[0]?.url || 'https://via.placeholder.com/100x100/E91E63/white?text=U2'
  });

  // Generate quiz questions (same algorithm as single-player)
  const generateQuizQuestions = (): QuizTrack[] => {
    const mauricioTracks = myTracks.map(track => ({
      id: track.id,
      name: track.name,
      artist: track.artists[0].name,
      albumImage: track.album.images[0]?.url,
      owner: 'mauricio' as const,
      ownerName: myProfile?.display_name || 'Mauricio'
    }));

    const noviaTracks = herTracks.map(track => ({
      id: track.id,
      name: track.name,
      artist: track.artists[0].name,
      albumImage: track.album.images[0]?.url,
      owner: 'novia' as const,
      ownerName: herProfile?.display_name || 'Tu Novia'
    }));

    // Find shared songs
    const sharedTracks: QuizTrack[] = [];
    mauricioTracks.forEach(mTrack => {
      const matching = noviaTracks.find(nTrack =>
        mTrack.id === nTrack.id ||
        (mTrack.name.toLowerCase() === nTrack.name.toLowerCase() &&
         mTrack.artist.toLowerCase() === nTrack.artist.toLowerCase())
      );
      if (matching) {
        const randomOwner = Math.random() > 0.5 ? mTrack : matching;
        sharedTracks.push(randomOwner);
      }
    });

    // Get unique tracks
    const uniqueMauricioTracks = mauricioTracks.filter(mTrack =>
      !sharedTracks.some(sTrack =>
        sTrack.id === mTrack.id ||
        (sTrack.name.toLowerCase() === mTrack.name.toLowerCase() &&
         sTrack.artist.toLowerCase() === mTrack.artist.toLowerCase())
      )
    );

    const uniqueNoviaTracks = noviaTracks.filter(nTrack =>
      !sharedTracks.some(sTrack =>
        sTrack.id === nTrack.id ||
        (sTrack.name.toLowerCase() === nTrack.name.toLowerCase() &&
         sTrack.artist.toLowerCase() === nTrack.artist.toLowerCase())
      )
    );

    // Prioritize shared songs (4-5 questions), mix in random ones (2-3 questions)
    const shuffledShared = sharedTracks.sort(() => Math.random() - 0.5);
    const shuffledUnique = [...uniqueMauricioTracks, ...uniqueNoviaTracks].sort(() => Math.random() - 0.5);

    const sharedCount = Math.min(5, sharedTracks.length);
    const randomCount = 7 - sharedCount;

    const finalQuiz = [
      ...shuffledShared.slice(0, sharedCount),
      ...shuffledUnique.slice(0, randomCount)
    ].sort(() => Math.random() - 0.5);

    return finalQuiz.slice(0, 7);
  };

  // Create new session (or join existing one)
  const createSession = async (): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      // Use a fixed session ID so everyone joins the same lobby
      const fixedSessionId = 'coopQuiz_current_session';
      const sessionRef = doc(db, 'coopQuizSessions', fixedSessionId);

      // Check if session already exists
      const existingSession = await getDoc(sessionRef);

      if (existingSession.exists()) {
        // Session exists, just join it
        console.log('✅ Joining existing co-op session:', fixedSessionId);
        setSessionId(fixedSessionId);
        setLoading(false);
        return fixedSessionId;
      }

      // Session doesn't exist, create a new one
      const questions = generateQuizQuestions();

      const newSession: CoopQuizSession = {
        sessionId: fixedSessionId,
        status: 'waiting',
        createdAt: serverTimestamp(),
        questions,
        currentQuestion: 0,
        players: {
          player1: {
            joined: false,
            ready: false,
            userId: ''
          },
          player2: {
            joined: false,
            ready: false,
            userId: ''
          }
        },
        answers: {},
        scores: {
          player1: 0,
          player2: 0
        }
      };

      await setDoc(sessionRef, newSession);

      setSessionId(fixedSessionId);
      setLoading(false);
      console.log('✅ Co-op quiz session created:', fixedSessionId);

      return fixedSessionId;
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      console.error('❌ Error creating session:', err);
      throw err;
    }
  };

  // Join as player 1 or 2
  const joinAsPlayer = async (playerNum: 1 | 2) => {
    if (!sessionId) {
      setError('No session ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      const playerKey = `players.player${playerNum}`;
      const userId = `user_${Math.random().toString(36).substr(2, 9)}`;

      console.log(`🎯 [useCoopQuiz] Joining slot ${playerNum} with ID:`, userId);

      await updateDoc(sessionRef, {
        [`${playerKey}.joined`]: true,
        [`${playerKey}.userId`]: userId
      });

      setMyPlayerNumber(playerNum);
      setLoading(false);
      console.log(`✅ Joined as player ${playerNum} with ID:`, userId);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      console.error('❌ Error joining as player:', err);
    }
  };

  // Set ready state
  const setReady = async () => {
    if (!sessionId || !myPlayerNumber) {
      setError('No session or player number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      const playerKey = `players.player${myPlayerNumber}`;

      await updateDoc(sessionRef, {
        [`${playerKey}.ready`]: true
      });

      setLoading(false);
      console.log(`✅ Player ${myPlayerNumber} is ready`);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      console.error('❌ Error setting ready:', err);
    }
  };

  // Submit answer
  const submitAnswer = async (answer: 'mauricio' | 'novia') => {
    if (!sessionId || !myPlayerNumber || !session) {
      setError('No session, player number, or session data');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      const questionKey = `question${session.currentQuestion}`;
      const playerKey = `player${myPlayerNumber}`;

      await updateDoc(sessionRef, {
        [`answers.${questionKey}.${playerKey}`]: answer
      });

      setLoading(false);
      console.log(`✅ Answer submitted for question ${session.currentQuestion}`);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      console.error('❌ Error submitting answer:', err);
    }
  };

  // Advance to next question (called after both players answer)
  const advanceQuestion = async () => {
    if (!sessionId || !session) return;

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      const newQuestionNum = session.currentQuestion + 1;

      if (newQuestionNum >= session.questions.length) {
        // Game finished
        await updateDoc(sessionRef, {
          status: 'finished'
        });
        console.log('✅ Game finished');
      } else {
        // Next question
        await updateDoc(sessionRef, {
          currentQuestion: newQuestionNum
        });
        console.log(`✅ Advanced to question ${newQuestionNum}`);
      }
    } catch (err: any) {
      console.error('❌ Error advancing question:', err);
    }
  };

  // Update scores after answers are submitted
  const updateScores = async (player1Correct: boolean, player2Correct: boolean) => {
    if (!sessionId || !session) return;

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      const newScores = {
        player1: session.scores.player1 + (player1Correct ? 1 : 0),
        player2: session.scores.player2 + (player2Correct ? 1 : 0)
      };

      await updateDoc(sessionRef, {
        'scores.player1': newScores.player1,
        'scores.player2': newScores.player2
      });

      console.log('✅ Scores updated:', newScores);
    } catch (err: any) {
      console.error('❌ Error updating scores:', err);
    }
  };

  // Start game (when both players ready)
  const startGame = async () => {
    if (!sessionId) return;

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      await updateDoc(sessionRef, {
        status: 'playing'
      });
      console.log('✅ Game started');
    } catch (err: any) {
      console.error('❌ Error starting game:', err);
    }
  };

  // Delete session
  const deleteSession = async () => {
    if (!sessionId) return;

    try {
      const sessionRef = doc(db, 'coopQuizSessions', sessionId);
      await deleteDoc(sessionRef);
      console.log('✅ Session deleted');
      setSessionId(null);
      setSession(null);
      setMyPlayerNumber(null);
    } catch (err: any) {
      console.error('❌ Error deleting session:', err);
    }
  };

  // Listen to session updates
  useEffect(() => {
    if (!sessionId) {
      console.log('🔴 [useCoopQuiz] No sessionId, not listening');
      return;
    }

    console.log('🎧 [useCoopQuiz] Setting up snapshot listener for session:', sessionId);
    const sessionRef = doc(db, 'coopQuizSessions', sessionId);
    const unsubscribe = onSnapshot(sessionRef, (snapshot) => {
      console.log('📸 [useCoopQuiz] Snapshot received - exists:', snapshot.exists());

      if (snapshot.exists()) {
        const data = snapshot.data() as CoopQuizSession;
        console.log('📦 [useCoopQuiz] Session data:', {
          status: data.status,
          currentQuestion: data.currentQuestion,
          player1Joined: data.players.player1.joined,
          player2Joined: data.players.player2.joined,
          player1Ready: data.players.player1.ready,
          player2Ready: data.players.player2.ready
        });
        setSession(data);

        // Auto-assign is handled by setMyPlayerNumber when joining
        // We don't need to auto-assign based on displayName anymore

        // Auto-start game when both players ready
        if (
          data.status === 'waiting' &&
          data.players.player1.joined &&
          data.players.player1.ready &&
          data.players.player2.joined &&
          data.players.player2.ready
        ) {
          console.log('🚀 [useCoopQuiz] Both players ready - starting game');
          startGame();
        }
      } else {
        console.log('🔴 [useCoopQuiz] Session does not exist');
        setSession(null);
      }
    });

    return () => {
      console.log('🔌 [useCoopQuiz] Unsubscribing from session updates');
      unsubscribe();
    };
  }, [sessionId, myPlayerNumber, myProfile]);

  return {
    session,
    sessionId,
    myPlayerNumber,
    loading,
    error,
    createSession,
    joinAsPlayer,
    setReady,
    submitAnswer,
    advanceQuestion,
    updateScores,
    deleteSession,
    getPlayer1DisplayData,
    getPlayer2DisplayData
  };
};
